import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Navigate } from "react-router-dom";
import "./App.css";

import AuthProvider from "./Auth/AuthProvider";
import useAuth from "./Auth/useAuth";
import SignUp from "./Pages/SignUp/SignUp";
import Login from "./Pages/Login/Login";
import HomePage from "./Pages/HomePage/HomePage";
import Loading from "./Components/Loading/Loading";
import Driver from "./Components/Driver/Driver";
import Profile from "./Components/Profile/Profile";
import Chat from "./Components/Chat/Chat";
import { ChatNotificationProvider } from "./Auth/ChatNotificationContext";

// Protected Route Component with better error handling
const ProtectedRoute = ({ element, requiredProfile, children }) => {
  const { isAuthenticated, userProfile, isLoading } = useAuth();
  
  console.log("ProtectedRoute check:", { isAuthenticated, userProfile, isLoading });
  
  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    console.log("Not authenticated, redirecting to login");
    return <Navigate to="/" />;
  }

  // Define allowed paths for driverAssociate profile
  const driverAllowedPaths = ["/driver", "/profile"];
  const currentPath = window.location.pathname;

  // If user is a driver and trying to access a path not in their allowed list
  if (userProfile === "driverAssociate" && !driverAllowedPaths.includes(currentPath)) {
    console.log("Driver profile detected, redirecting to driver page");
    return <Navigate to="/driver" />;
  }

  if (requiredProfile && userProfile !== requiredProfile) {
    console.log(`Profile mismatch: required ${requiredProfile}, has ${userProfile}`);
    return userProfile === "driverAssociate" ? (
      <Navigate to="/driver" />
    ) : (
      <Navigate to="/home" />
    );
  }

  console.log("Access granted to protected route");
  return element || children;
};

// Profile Route Wrapper - handles different layouts for different user types
const ProfileRouteWrapper = () => {
  const { userProfile } = useAuth();
  
  // If driver, show Profile without HomePage wrapper (no Navbar/Sidebar)
  if (userProfile === "driverAssociate") {
    return <Profile />;
  }
  
  // For other users, wrap Profile in HomePage (includes Navbar/Sidebar)
  return (
    <HomePage>
      <Profile />
    </HomePage>
  );
};

// Main App Content with Auth
const AppContent = () => {
  const { isLoading } = useAuth();
  
  console.log("AppContent render, isLoading:", isLoading);
  
  if (isLoading) {
    return <Loading />;
  }

  return (
    <Routes>
      {/* Public Routes - No authentication required */}
      <Route path="/" element={<Login />} />
      <Route path="/signup" element={<SignUp />} />
      
      {/* Protected Routes - Authentication required */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/home/:TripID"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/today-list"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/freight"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vehicle"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/vendor"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/meetings"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/transactions"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/generate-bill"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/business"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/revise-bill"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/driver"
        element={
          <ProtectedRoute requiredProfile="driverAssociate">
            <Driver />
          </ProtectedRoute>
        }
      />
      <Route
        path="/list-users"
        element={
          <ProtectedRoute requiredProfile="admin">
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfileRouteWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <HomePage>
              <Chat />
            </HomePage>
          </ProtectedRoute>
        }
      />
      <Route
        path="/order-book"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/task"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/approval"
        element={
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        }
      />
      
      {/* Fallback route - redirect to login */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

// Main App Component
function App() {
  console.log("App component rendering");
  
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <ChatNotificationProvider>
            <AppContent />
          </ChatNotificationProvider>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;